/**
 * Inline SVG icon set — single-colour line icons at 18×18 base with
 * 1.6 px stroke, rounded caps/joins. Matches the medical-imaging
 * look of MedDream / RadiAnt toolbars: minimal, geometric, clear
 * even at 14 px. Every icon takes an optional `size` override so the
 * same component works in the toolbar (18) and the viewport HUD (14).
 *
 * Icons are pure (no state) — render them inside a <button> for
 * interaction.
 */
import type { JSX } from "solid-js";

type IconProps = JSX.HTMLAttributes<SVGSVGElement> & { size?: number };

const defaults = {
  width: 18, height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": 1.6,
  "stroke-linecap": "round",
  "stroke-linejoin": "round",
} as const;

function base(props: IconProps, children: JSX.Element): JSX.Element {
  const { size, ...rest } = props;
  return (
    <svg {...defaults} {...rest}
         width={size ?? defaults.width}
         height={size ?? defaults.height}>
      {children}
    </svg>
  );
}

/* ---------- Viewport / navigation ---------- */
export const IconPan = (p: IconProps) => base(p,
  <>
    <path d="M12 3v10M12 21v-3M3 12h10M21 12h-3" />
    <path d="M9 6l3-3 3 3M6 9l-3 3 3 3M15 18l-3 3-3-3M18 15l3-3-3-3" />
  </>);

export const IconZoomIn = (p: IconProps) => base(p,
  <>
    <circle cx="11" cy="11" r="6" />
    <line x1="11" y1="8" x2="11" y2="14" />
    <line x1="8" y1="11" x2="14" y2="11" />
    <line x1="15.5" y1="15.5" x2="20" y2="20" />
  </>);

export const IconZoomOut = (p: IconProps) => base(p,
  <>
    <circle cx="11" cy="11" r="6" />
    <line x1="8" y1="11" x2="14" y2="11" />
    <line x1="15.5" y1="15.5" x2="20" y2="20" />
  </>);

export const IconReset = (p: IconProps) => base(p,
  <>
    <path d="M4 8 V4 H8" />
    <path d="M20 8 V4 H16" />
    <path d="M4 16 V20 H8" />
    <path d="M20 16 V20 H16" />
    <rect x="9" y="9" width="6" height="6" rx="1" />
  </>);

export const IconRotate = (p: IconProps) => base(p,
  <>
    <path d="M21 12A9 9 0 1 1 12 3" />
    <polyline points="21 3 21 9 15 9" />
  </>);

/* ---------- Windowing / contrast ---------- */
export const IconWindow = (p: IconProps) => base(p,
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3 a9 9 0 0 0 0 18Z" fill="currentColor" stroke="none" />
  </>);

export const IconInvert = (p: IconProps) => base(p,
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3 v18" />
  </>);

export const IconFlipH = (p: IconProps) => base(p,
  <>
    <path d="M12 3v18" />
    <path d="M8 7L4 12l4 5" />
    <path d="M16 7l4 5-4 5" />
  </>);

export const IconFlipV = (p: IconProps) => base(p,
  <>
    <path d="M3 12h18" />
    <path d="M7 8L12 4l5 4" />
    <path d="M7 16l5 4 5-4" />
  </>);

export const IconPreset = (p: IconProps) => base(p,
  <>
    <rect x="3" y="6" width="18" height="4" rx="1" />
    <rect x="3" y="14" width="12" height="4" rx="1" />
  </>);

/* ---------- Measurement ---------- */
export const IconLength = (p: IconProps) => base(p,
  <>
    <line x1="4" y1="20" x2="20" y2="4" />
    <circle cx="4" cy="20" r="1.6" fill="currentColor" />
    <circle cx="20" cy="4" r="1.6" fill="currentColor" />
    <line x1="7" y1="14" x2="10" y2="17" />
    <line x1="11" y1="10" x2="14" y2="13" />
    <line x1="15" y1="6" x2="18" y2="9" />
  </>);

export const IconAngle = (p: IconProps) => base(p,
  <>
    <path d="M4 20 L4 4 L20 20" />
    <path d="M4 12 A8 8 0 0 0 12 20" />
    <circle cx="4" cy="4" r="1.5" fill="currentColor" />
    <circle cx="4" cy="20" r="1.5" fill="currentColor" />
    <circle cx="20" cy="20" r="1.5" fill="currentColor" />
  </>);

export const IconCobb = (p: IconProps) => base(p,
  <>
    <line x1="3" y1="7" x2="21" y2="5" />
    <line x1="3" y1="19" x2="21" y2="17" />
    <circle cx="3" cy="7" r="1.3" fill="currentColor" />
    <circle cx="21" cy="5" r="1.3" fill="currentColor" />
    <circle cx="3" cy="19" r="1.3" fill="currentColor" />
    <circle cx="21" cy="17" r="1.3" fill="currentColor" />
  </>);

export const IconEllipse = (p: IconProps) => base(p,
  <>
    <ellipse cx="12" cy="12" rx="9" ry="6" />
    <circle cx="3" cy="12" r="1.4" fill="currentColor" />
    <circle cx="21" cy="12" r="1.4" fill="currentColor" />
  </>);

export const IconRect = (p: IconProps) => base(p,
  <>
    <rect x="4" y="6" width="16" height="12" rx="1" />
    <circle cx="4" cy="6" r="1.4" fill="currentColor" />
    <circle cx="20" cy="18" r="1.4" fill="currentColor" />
  </>);

export const IconFreehand = (p: IconProps) => base(p,
  <path d="M4 18c3-6 5-11 9-8 3 2 0 6 2 7s4-7 5-10" />);

export const IconProbe = (p: IconProps) => base(p,
  <>
    <circle cx="12" cy="10" r="3" />
    <path d="M12 13 V20" />
  </>);

/* ---------- Cine / playback ---------- */
export const IconPlay = (p: IconProps) => base(p,
  <polygon points="7 5 19 12 7 19" fill="currentColor" stroke="none" />);

export const IconPause = (p: IconProps) => base(p,
  <>
    <rect x="7" y="5" width="3.5" height="14" rx="0.5" fill="currentColor" stroke="none" />
    <rect x="13.5" y="5" width="3.5" height="14" rx="0.5" fill="currentColor" stroke="none" />
  </>);

export const IconStackUp = (p: IconProps) => base(p,
  <>
    <polyline points="6 10 12 4 18 10" />
    <path d="M12 4 V20" />
  </>);
export const IconStackDown = (p: IconProps) => base(p,
  <>
    <polyline points="6 14 12 20 18 14" />
    <path d="M12 20 V4" />
  </>);

/* ---------- MPR ---------- */
export const IconMPR = (p: IconProps) => base(p,
  <>
    <rect x="3" y="3" width="8" height="8" />
    <rect x="13" y="3" width="8" height="8" />
    <rect x="3" y="13" width="18" height="8" />
  </>);

export const IconCrosshair = (p: IconProps) => base(p,
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3 V9" />
    <path d="M12 15 V21" />
    <path d="M3 12 H9" />
    <path d="M15 12 H21" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
  </>);

/* ---------- Actions ---------- */
export const IconSave = (p: IconProps) => base(p,
  <>
    <path d="M5 3h11l3 3v15H5z" />
    <path d="M7 3v5h9V3" />
    <path d="M7 13h10v7H7z" />
  </>);

export const IconSign = (p: IconProps) => base(p,
  <>
    <path d="M3 20s3-3 6-3 3 3 6 3 6-3 6-3" />
    <path d="M4 14l4-8 4 4 4-6 4 6" />
  </>);

export const IconTrash = (p: IconProps) => base(p,
  <>
    <path d="M4 7h16" />
    <path d="M9 7V4h6v3" />
    <path d="M6 7l1 13h10l1-13" />
  </>);

export const IconHelp = (p: IconProps) => base(p,
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9 9a3 3 0 1 1 5.3 2c-0.5 0.4-2.3 1.2-2.3 3" />
    <circle cx="12" cy="17" r="0.7" fill="currentColor" />
  </>);

export const IconClose = (p: IconProps) => base(p,
  <>
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </>);

/* ---------- ML labeling ---------- */
export const IconBbox = (p: IconProps) => base(p,
  <>
    <rect x="4" y="4" width="16" height="16" />
    <rect x="4" y="4" width="2" height="2" fill="currentColor" stroke="none" />
    <rect x="18" y="4" width="2" height="2" fill="currentColor" stroke="none" />
    <rect x="4" y="18" width="2" height="2" fill="currentColor" stroke="none" />
    <rect x="18" y="18" width="2" height="2" fill="currentColor" stroke="none" />
  </>);

export const IconPolygon = (p: IconProps) => base(p,
  <>
    <polygon points="4 11 9 4 18 6 20 15 13 20 6 17" />
    <circle cx="4" cy="11" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="9" cy="4" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="18" cy="6" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="20" cy="15" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="13" cy="20" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="6" cy="17" r="1.4" fill="currentColor" stroke="none" />
  </>);

export const IconKeypoint = (p: IconProps) => base(p,
  <>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none" />
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
  </>);

export const IconBrush = (p: IconProps) => base(p,
  <>
    <path d="M14 4 L20 10 L11 19 C9 21 5 21 4 20 C3 19 3 15 5 13 Z" />
    <path d="M14 4 L18 8" stroke-linecap="round" />
    <circle cx="7" cy="17" r="1" fill="currentColor" stroke="none" />
  </>);

/* ---------- Text labeling ---------- */
export const IconText = (p: IconProps) => base(p,
  <>
    <line x1="5" y1="5" x2="19" y2="5" />
    <line x1="12" y1="5" x2="12" y2="20" />
    <line x1="9" y1="20" x2="15" y2="20" />
  </>);

export const IconArrow = (p: IconProps) => base(p,
  <>
    <line x1="5" y1="19" x2="19" y2="5" />
    <polyline points="19 13 19 5 11 5" />
  </>);

export const IconPin = (p: IconProps) => base(p,
  <>
    <path d="M12 3 C8 3 5.5 5.8 5.5 9 C5.5 12.8 12 21 12 21 C12 21 18.5 12.8 18.5 9 C18.5 5.8 16 3 12 3 Z" />
    <circle cx="12" cy="9" r="2.4" fill="currentColor" stroke="none" />
  </>);

/* ---------- Chrome / role ---------- */
export const IconLogout = (p: IconProps) => base(p,
  <>
    <path d="M15 3h5v18h-5" />
    <path d="M3 12h13" />
    <polyline points="10 8 14 12 10 16" />
  </>);

export const IconSearch = (p: IconProps) => base(p,
  <>
    <circle cx="11" cy="11" r="6" />
    <line x1="15.5" y1="15.5" x2="20" y2="20" />
  </>);

/* ---------- Endoscopy domain ----------
 * Custom glyphs for the endoscopy workstation. Generic enough to
 * cover flexible (GI, bronch, capsule) and rigid (laryngo, cysto,
 * arthro) scopes — explicitly not a lung, not a GI-specific organ. */

/** Generic scope — angled handpiece + coiled insertion tube + distal
 *  lens tip. Used in the left-nav Endoscopy entry and anywhere the
 *  specialty needs a single-glyph brand mark. */
export const IconScope = (p: IconProps) => base(p,
  <>
    {/* Control handle */}
    <rect x="3"  y="3"  width="5.5" height="6" rx="1.2" />
    <circle cx="5.75" cy="6" r="1" fill="currentColor" stroke="none" />
    {/* Umbilical — short horizontal stub from handle */}
    <path d="M8.5 6.5 h2.5 a2 2 0 0 1 2 2 v0" />
    {/* Coiled, flexible insertion tube */}
    <path d="M13 8.5 c0 3 -4 3.5 -4 6.5 s4 3.5 7 5" />
    {/* Distal tip with a lens */}
    <rect x="16" y="18" width="5" height="3" rx="0.6"
          transform="rotate(18 18.5 19.5)" />
    <circle cx="20.6" cy="20.4" r="0.9" />
  </>);

/** Anatomy — stylised torso with a highlighted labeled region.
 *  Reads as "landmark on the body" independent of GI/airway/urinary. */
export const IconAnatomy = (p: IconProps) => base(p,
  <>
    <circle cx="12" cy="5.25" r="2.25" />
    <path d="M6.5 10 c0 -1.5 2 -2 5.5 -2 s5.5 0.5 5.5 2 v2 c0 3 -1.5 5 -5.5 5 s-5.5 -2 -5.5 -5 z" />
    <path d="M9.5 17 v2.5" />
    <path d="M14.5 17 v2.5" />
    {/* Highlighted labeled region — the "span" the viewer attaches */}
    <rect x="9" y="10.25" width="6" height="3.25" rx="0.5"
          fill="currentColor" stroke="none" opacity="0.25" />
    <rect x="9" y="10.25" width="6" height="3.25" rx="0.5" />
  </>);

/** Lesion — concentric target with reticle. Reads as "focused
 *  finding" without the cartoon-y bullseye of the 🎯 emoji. */
export const IconLesion = (p: IconProps) => base(p,
  <>
    <circle cx="12" cy="12" r="7.5" />
    <circle cx="12" cy="12" r="4.25" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <path d="M12 2.5 v2.5" />
    <path d="M12 19 v2.5" />
    <path d="M2.5 12 h2.5" />
    <path d="M19 12 h2.5" />
  </>);

/** Camera — for the Still-capture button. */
export const IconCamera = (p: IconProps) => base(p,
  <>
    <path d="M4 8 h3 l1.5 -2 h7 l1.5 2 h3 a1 1 0 0 1 1 1 v9 a1 1 0 0 1 -1 1 h-16 a1 1 0 0 1 -1 -1 v-9 a1 1 0 0 1 1 -1 z" />
    <circle cx="12" cy="13.5" r="3.5" />
    <circle cx="12" cy="13.5" r="1.4" fill="currentColor" stroke="none" />
  </>);

/** Pencil edit — for editable span rows. */
export const IconEdit = (p: IconProps) => base(p,
  <>
    <path d="M4 20 l4 -1 11 -11 -3 -3 -11 11 -1 4 z" />
    <path d="M14 7 l3 3" />
  </>);

/** Signed / finalised badge — checkmark ribbon. */
export const IconSigned = (p: IconProps) => base(p,
  <>
    <path d="M7 3 h10 a1 1 0 0 1 1 1 v17 l-6 -3 -6 3 v-17 a1 1 0 0 1 1 -1 z" />
    <path d="M9 11 l2 2 4 -4" />
  </>);

/** Step-back / step-forward glyphs for the transport bar (replaces
 *  the ⏮/⏭ emojis which render inconsistently across fonts). */
export const IconStepBack = (p: IconProps) => base(p,
  <>
    <path d="M17 5 L8 12 L17 19 Z" fill="currentColor" stroke="currentColor" />
    <path d="M6 4.5 v15" />
  </>);
export const IconStepFwd = (p: IconProps) => base(p,
  <>
    <path d="M7 5 L16 12 L7 19 Z" fill="currentColor" stroke="currentColor" />
    <path d="M18 4.5 v15" />
  </>);

/* ---------- 3D volume / cube — used by the 2D↔3D toggle ---------- */
export const Icon3D = (p: IconProps) => base(p,
  <>
    <path d="M12 3 L21 8 L21 16 L12 21 L3 16 L3 8 Z" />
    <path d="M12 3 L12 21" />
    <path d="M3 8 L21 8" />
    <path d="M3 16 L21 16" />
  </>);

/* ---------- Folder-tree icons (left-panel consolidation) ---------- */
// Disclosure chevron — rotates via CSS when the branch is open.
export const IconChevronRight = (p: IconProps) => base(p,
  <path d="M9 6 L15 12 L9 18" />);

// Folder case — top tab + body. Represents a patient case / study.
export const IconCase = (p: IconProps) => base(p,
  <>
    <path d="M3 7 V19 A1 1 0 0 0 4 20 H20 A1 1 0 0 0 21 19 V9 A1 1 0 0 0 20 8 H12 L10 5 H4 A1 1 0 0 0 3 6 Z" />
    <path d="M8 14 H16" />
  </>);

// Slide — rectangular glass slide with diagonal specimen.
export const IconSlide = (p: IconProps) => base(p,
  <>
    <rect x="3"  y="6"  width="18" height="12" rx="1" />
    <path d="M7 10 L17 14" />
    <circle cx="9"  cy="11" r="0.8" fill="currentColor" stroke="none" />
    <circle cx="15" cy="13" r="0.8" fill="currentColor" stroke="none" />
  </>);

// Palette — classification colours. Three dots on a rounded tray.
export const IconPalette = (p: IconProps) => base(p,
  <>
    <path d="M12 3 A9 9 0 1 0 12 21 A2 2 0 0 1 14 19 A2 2 0 0 1 16 17 L17 17 A4 4 0 0 0 21 13 A10 10 0 0 0 12 3 Z" />
    <circle cx="7.5"  cy="11" r="1" fill="currentColor" stroke="none" />
    <circle cx="10.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="15"   cy="8" r="1" fill="currentColor" stroke="none" />
  </>);

// Microscope — small objective head. Used as case-panel accent.
export const IconMicroscope = (p: IconProps) => base(p,
  <>
    <path d="M8 3 L12 3 L13 6 L9 6 Z" />
    <path d="M11 6 L11 13" />
    <path d="M9 13 L13 13" />
    <path d="M5 20 L19 20" />
    <path d="M6 17 A6 6 0 0 1 18 17" />
    <path d="M6 17 L5 20" />
    <path d="M18 17 L19 20" />
  </>);

// Ruler — magnification / scale ladder accent.
export const IconRuler = (p: IconProps) => base(p,
  <>
    <rect x="3" y="8" width="18" height="8" rx="1" />
    <path d="M6 8 V11 M9 8 V13 M12 8 V11 M15 8 V13 M18 8 V11" />
  </>);
